import { baseApi } from './baseApi';
import {
  parseCreateSupportRequestResponse,
  parseSupportRequestsListResponse,
  parseSupportErrorResponse,
  parseSupportListResponse
} from '../parsers/support.parsers';

/**
 * Generate unique support request ID
 */
const generateSupportRequestId = () => {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 7);
  return `SR-${timestamp}-${random}`.toUpperCase();
};

/**
 * Generate random UUID for IDs
 */
const generateId = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Generate mock support requests
 */
const generateMockSupportRequests = (count = 5) => {
  const statuses = ['open', 'in_progress', 'resolved', 'closed'];
  const queries = [
    'How to change my profile settings?',
    'Unable to login to my account',
    'Transaction pending for 3 days',
    'Need help with investment options',
    'Forgot password, need reset',
    'Account verification issue',
    'Update bank account details',
    'How to redeem investments?',
    'App not loading properly',
    'Requesting portfolio statement'
  ];

  const requests = [];
  for (let i = 0; i < count; i++) {
    const daysAgo = Math.floor(Math.random() * 30);
    const createdAt = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString();
    const updatedAt = new Date(Date.now() - Math.floor(Math.random() * daysAgo) * 24 * 60 * 60 * 1000).toISOString();

    requests.push({
      id: generateId(),
      query: queries[Math.floor(Math.random() * queries.length)],
      status: statuses[Math.floor(Math.random() * statuses.length)],
      createdAt,
      updatedAt
    });
  }

  return requests;
};

/**
 * Simulate network delay
 */
const delay = (ms = 800) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Support Request API service
 * Extends the base API with customer support endpoints
 * 
 * @use Support Request Flow
 * 1. Create support request with query text
 * 2. Receive unique request ID
 * 3. Fetch list of support requests with filtering
 * 
 * @example
 * const [createRequest] = useCreateSupportRequestMutation();
 * const { data } = await createRequest({ query: 'Need help with...' });
 */
export const supportApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Create a new support request
     * POST /support-request/
     * 
     * @purpose Create a new customer support request
     * @useCase User needs help, reporting issues, requesting assistance
     * 
     * @request {Object} payload
     * @request {string} payload.query - Support request message/question
     * 
     * @response {Object}
     * @response {number} code - HTTP status code (200)
     * @response {string} message - Success message
     * @response {Object} result.id - Unique support request ID
     * 
     * @mock Returns unique ID (SR-XXXXX) after 700-1000ms delay
     */
    createSupportRequest: builder.mutation({
      query: (payload) => ({
        url: '/support-request/',
        method: 'POST',
        body: payload,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: async (response) => {
        // Simulate network delay
        await delay(Math.random() * 300 + 700);

        return parseCreateSupportRequestResponse(response);
      },
      transformErrorResponse: (error) => parseSupportErrorResponse(error),
      invalidatesTags: ['SupportRequest'],
    }),

    /**
     * Get list of support requests
     * GET /support-request/
     * 
     * @purpose Retrieve user's support requests with filtering and pagination
     * @useCase View support history, check request status
     * 
     * @request {Object} params
     * @request {string} params.status - Filter by status ('open', 'in_progress', 'resolved', 'closed')
     * @request {number} params.page - Page number (default: 1)
     * @request {number} params.limit - Items per page (default: 20)
     * 
     * @response {Object}
     * @response {number} code - HTTP status code (200)
     * @response {string} message - Success message
     * @response {Object} result - Paginated data
     * @response {number} result.page - Current page number
     * @response {number} result.limit - Items per page
     * @response {number} result.total - Total items
     * @response {Array} result.data - Array of support requests
     * @response {string} result.data[].id - Request ID
     * @response {string} result.data[].query - Request message
     * @response {string} result.data[].status - Request status
     * @response {string} result.data[].createdAt - Creation timestamp
     * @response {string} result.data[].updatedAt - Last update timestamp
     * 
     * @mock Returns paginated mock data after 600-900ms delay
     */
    getSupportRequests: builder.query({
      query: (params = {}) => ({
        url: '/support-request/',
        method: 'GET',
        params: {
          status: params.status || 'all',
          page: params.page || 1,
          limit: params.limit || 20,
        },
      }),
      transformResponse: async (response) => {
        // Simulate network delay
        await delay(Math.random() * 300 + 600);

        return parseSupportRequestsListResponse(response);
      },
      transformErrorResponse: (error) => parseSupportErrorResponse(error),
      providesTags: ['SupportRequest'],
    }),

    /**
     * Submit support request
     * POST /api/support
     * 
     * @purpose Submit a support request with request message and category
     * @useCase User needs help from service request modal
     * 
     * @request {Object} payload
     * @request {string} payload.request - Support request message
     * @request {string} payload.category - Category of the request (e.g., 'INVESTMENTS', 'PORTFOLIO', 'PROFILE', 'SIP', 'MUTUAL FUNDS')
     * 
     * @response {Object} API response
     */
    submitSupportRequest: builder.mutation({
      query: (payload) => ({
        url: '/api/support',
        method: 'POST',
        body: {
          request: payload.request,
          category: payload.category,
          reason: payload.reason
        }
      }),
      transformErrorResponse: (error) => parseSupportErrorResponse(error),
      invalidatesTags: ['SupportRequest'],
    }),

    /**
     * Get support requests list
     * GET /api/support
     * 
     * @purpose Retrieve support requests with pagination and date filtering
     * @useCase View help center tickets with filtering
     * 
     * @request {Object} params
     * @request {number} params.page - Page number (default: 1)
     * @request {number} params.limit - Items per page (default: 10)
     * @request {string} params.startDate - Start date in ISO format (e.g., '2024-01-01T00:00:00.000Z')
     * @request {string} params.endDate - End date in ISO format (e.g., '2024-12-31T23:59:59.999Z')
     * 
     * @response {Object}
     * @response {string} code - Response code (e.g., 'SUPS0001')
     * @response {string} message - Success message
     * @response {Object} result - Response data
     * @response {Array} result.data - Array of support requests
     * @response {Object} result.pagination - Pagination info
     */
    getSupportList: builder.query({
      query: (params = {}) => ({
        url: '/api/support',
        method: 'GET',
        params: {
          page: params.page || 1,
          limit: params.limit || 10,
          ...(params.startDate && { startDate: params.startDate }),
          ...(params.endDate && { endDate: params.endDate })
        }
      }),
      transformResponse: (response) => parseSupportListResponse(response),
      transformErrorResponse: (error) => parseSupportErrorResponse(error),
      providesTags: ['SupportRequest'],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useCreateSupportRequestMutation,
  useGetSupportRequestsQuery,
  useSubmitSupportRequestMutation,
  useGetSupportListQuery,
} = supportApi;

